import {RecipientGiftVisibility} from "home/landing/RecipientGiftVisibility";
import {useContext, useId, useState} from "react";
import type {ReferralOffer} from "download/acquisitionInvite";
import {useLocale, useMessages} from "i18n";
import {useReferralIntroPrices} from "referral/useReferralIntroPrices";
import {formatMoney} from "pricing/PricingStorage";
import {PeelTag} from "peel/PeelTag";
import {TagAction} from "peel/PeelTag.styles";
import {
	OfferActions,
	OfferBadge,
	OfferBody,
	OfferCode,
	OfferCodeGroup,
	OfferCodeLabel,
	OfferCta,
	OfferHeadline,
	OfferPopover,
	ReferralOfferPanel,
} from "./ReferralOfferDisclosure.styles";
import {useRecipientOfferEvents} from "./useRecipientOfferEvents";

interface ReferralOfferDisclosureProps {
	/** The offer to present, already decided and built by `download/acquisitionInvite.ts`. */
	offer: ReferralOffer;
	/**
	 * The sender's display NAME, or `null` when the transfer carries none (ui#1693).
	 *
	 * `download/senderIdentity.ts` answers `null` for an address — including an account whose display
	 * name IS its address — so this prop can only ever be a name. The catalog owns both the named and
	 * the anonymous sentence; passing the raw value keeps the choice between them in one place per
	 * language rather than in this component.
	 */
	senderName: string | null;
}

/** The panel's test hook. Unchanged from the embedded panel's, so its e2e assertions keep their name. */
export const REFERRAL_OFFER_TESTID = "recipient-referral-offer";
/** The trigger's test hook — the element that now stands where the panel used to. */
export const REFERRAL_OFFER_TRIGGER_TESTID = "recipient-referral-trigger";

/** The gift face opens the existing priced disclosure without leaving a live download. */
export default function ReferralOfferDisclosure(
	{offer, senderName}: ReferralOfferDisclosureProps,
) {
	const m = useMessages().download.referralOffer;
	const introPrices = useReferralIntroPrices();
	const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
	// Fit can hide a mounted tag. Only the measured visible state counts as shown.
	const {reportOpened, reportClaimed} = useRecipientOfferEvents(useContext(RecipientGiftVisibility));

	// Do not promise a gift on a storefront with no configured intro prices.
	if (introPrices === null) {
		return null;
	}

	return (
		<>
			<PeelTag title={m.headline(senderName)} action={
				<TagAction type="button" data-testid={REFERRAL_OFFER_TRIGGER_TESTID}
					aria-haspopup="dialog" aria-expanded={anchor !== null} onClick={event => {
						setAnchor(event.currentTarget);
						reportOpened();
					}}>{m.triggerLabel}</TagAction>
			}/>
			<ReferralOfferPopover offer={offer} senderName={senderName} anchor={anchor}
				onClose={() => setAnchor(null)} onClaimed={reportClaimed}/>
		</>
	);
}

/**
 * The priced disclosure itself, anchored to whichever control opened it: the gift tag's own button, or
 * the recipient arcade's finish screen ("Claim your friend's gift", kitecorp/transferify-ui#2889), which
 * opens this same panel rather than describing the gift in words of its own. Renders nothing on a
 * storefront with no intro prices, for the reason the tag does.
 */
export function ReferralOfferPopover({offer, senderName, anchor, onClose, onClaimed}: ReferralOfferDisclosureProps & {
	anchor: HTMLElement | null;
	onClose(): void;
	/** The claim link was followed. */
	onClaimed(): void;
}) {
	const m = useMessages().download.referralOffer;
	const {locale} = useLocale();
	const introPrices = useReferralIntroPrices();
	// Each mounted disclosure owns its own label relationship; ids are document-global once the paper is
	// portalled out of the card, so a shared literal would make the accessible name global with it.
	const headlineId = useId();
	if (introPrices === null) {
		return null;
	}
	return (
		<OfferPopover
			open={anchor !== null}
			anchorEl={anchor}
			onClose={onClose}
			// Dropped from the trigger's bottom-left corner. MUI flips or slides it back into view by
			// itself when the card sits low in the viewport, which is where this trigger always is.
			anchorOrigin={{vertical: "bottom", horizontal: "left"}}
			transformOrigin={{vertical: "top", horizontal: "left"}}
			slotProps={{paper: {role: "dialog", "aria-labelledby": headlineId}}}
		>
			<ReferralOfferPanel data-testid={REFERRAL_OFFER_TESTID}>
				<OfferBadge>{m.badge}</OfferBadge>
				<OfferHeadline id={headlineId}>{m.headline(senderName)}</OfferHeadline>
				{/*
				  * The reward's own figures come from the baked catalogue, never from the sentence
				  * (api#1228, ui#1283). The whole disclosure is absent on a storefront that prices
				  * neither tier — see the early return above — so this line always has its amounts.
				  */}
				<OfferBody>
					{m.body(
						offer.windowDays,
						introPrices.introDays,
						formatMoney(introPrices.currency, introPrices.plus, locale),
						formatMoney(introPrices.currency, introPrices.premium, locale),
					)}
				</OfferBody>
				<OfferActions>
					{/*
					  * ui#1223: the label is VISIBLE text, never an aria-label on the <code>. `code`
					  * is a name-from-author-prohibited role, so the attribute was ignored by
					  * assistive technology and flagged by axe — and a bare 8-character string
					  * explained nothing to a sighted reader either.
					  */}
					<OfferCodeGroup>
						<OfferCodeLabel>{m.codeLabel}</OfferCodeLabel>
						<OfferCode>{offer.code}</OfferCode>
					</OfferCodeGroup>
					{/*
					  * `target="_blank"` is load-bearing rather than habit: this page may be
					  * mid-transfer. A LIVE peer-to-peer receive dies the moment this tab navigates,
					  * and a hosted download in the service worker goes with it — the offer may not
					  * cost the recipient the files they came for.
					  *
					  * `onClick` reports and returns; it never delays or intercepts the navigation.
					  * The beacon behind it is a `keepalive` fetch, which is specified to outlive the
					  * document that started it, so the report survives the new tab opening without
					  * anything here having to wait for it (ui#1973).
					  */}
					<OfferCta href={offer.href} target="_blank" rel="noopener" onClick={onClaimed}>
						{m.cta(offer.host)}
					</OfferCta>
				</OfferActions>
			</ReferralOfferPanel>
		</OfferPopover>
	);
}
